import { Country } from "../../types";
import { Environment } from "../../types";
import { TLD } from "../../types/TLD.types";
import { envMap, legacyEnvMap, URLs } from "../../types/urlUtils.types";

/**
 * Returns Hagerty origin
 * Example: https://www.hagerty.com
 */
function getOrigin(env: Environment, tld: TLD): string {
  const subdomain = env === "prod" ? "www" : envMap[env];
  return `https://${subdomain}.hagerty.${tld}`;
}

/**
 * Returns legacy Hagerty origin with subdomain
 * Example: https://account.hagerty.com
 */
function getLegacyOrigin(env: Environment, subdomain: string): string {
  return `https://${subdomain}${
    env === "prod" ? "" : `.${legacyEnvMap[env]}`
  }.hagerty.com`;
}

function getGlobalNavDataApiUrl(
  env: Environment,
  country: Country,
  location: "header" | "footer"
): string {
  const site = country === "CANADA" ? "cadirect" : "usdirect";
  // Locally use public api so we don't have to connect to run spa
  const baseUrl =
    env === "localhost" || env === "prod"
      ? "https://api.hagerty.com"
      : `https://${envMap[env]}api.hagerty.com`;

  // isFqurl is used to force the API to return fully qualified URLs (static links)
  return `${baseUrl}/gns/v1/${location}/${site}?isFqurl=true`;
}

/**
 * Returns an object that contains functions for requesting all the SPA's URLs.
 * Uses a closure to "close" over the args passed in.
 * This shortens the function signatures below to eliminate the need for
 * importing hooks at each call site to retrieve env and tld info.
 */
export function getUrls(env: Environment, siteTld: TLD): URLs {
  const usOrigin = getOrigin(env, "com");
  const caOrigin = getOrigin(env, "ca");
  const hagertyOrigin = siteTld === "com" ? usOrigin : caOrigin;
  const authOrigin = `https://${envMap[env]}login.hagerty.com`;
  const selfServiceOrigin = getLegacyOrigin(env, "account");
  const paymentsOrigin = getLegacyOrigin(env, "payments");

  const getOriginByCountry = (country: Country): string => {
    return country === "USA" ? usOrigin : caOrigin;
  };

  const urls: URLs = {
    home: hagertyOrigin,
    headerDataApi: (country: Country) =>
      getGlobalNavDataApiUrl(env, country, "header"),
    footerDataApi: (country: Country) =>
      getGlobalNavDataApiUrl(env, country, "footer"),
    billing: {
      managePayments: paymentsOrigin,
      payBill: paymentsOrigin,
    },
    hdc: {
      seeAllPlans: `${hagertyOrigin}/drivers-club/roadside-plans`,
      redeemBenefits:
        siteTld === "com"
          ? `${hagertyOrigin}/drivers-club/benefits`
          : `${hagertyOrigin}/drivers-club`,
      upgradeOrCancel: `${usOrigin}/drivers-club/my-account`,
    },
    insurance: {
      idCardPage: `${selfServiceOrigin}/documents/id-cards`,
      addVehicle: (policyNumber, policyCountry, addVehicleFlag) => {
        if (addVehicleFlag === "base") {
          const baseUrl = getOriginByCountry(policyCountry);
          return `${baseUrl}/Redirect/PolicyAction/${policyNumber}/addvehicle`;
        }

        return `${selfServiceOrigin}/policies/${policyNumber}/vehicles/add/1?source=${policyCountry}`;
      },
      editVehicle: (policyNumber, policyItemNumber, policyCountry) => {
        const baseUrl = getOriginByCountry(policyCountry);
        return `${baseUrl}/Redirect/PolicyAction/${policyNumber}/editvehicle?policyItemNumber=${policyItemNumber}`;
      },
      removeVehicle: (
        policyNumber,
        policyItemNumber,
        policyCountry,
        hasTooManyVehiclesOnSinglePolicy
      ) => {
        if (policyCountry === "CANADA" || hasTooManyVehiclesOnSinglePolicy) {
          const baseUrl = getOriginByCountry(policyCountry);
          return `${baseUrl}/Redirect/PolicyAction/${policyNumber}/RemoveVehicle?policyItemNumber=${policyItemNumber}`;
        }

        return `${selfServiceOrigin}/policies/${policyNumber}/vehicles/${policyItemNumber}/remove?source=usa`;
      },
      newFileClaim: (country) =>
        country === "USA" ? `${selfServiceOrigin}/claims` : "",
      fileClaim: (policyNumber, country) =>
        country === "USA"
          ? `${hagertyOrigin}/Redirect/PolicyAction/${policyNumber}/claims`
          : `${caOrigin}/claim/claim_main.aspx`,
      viewPolicy: (policyNumber, policyCountry, isStateFarm) => {
        if (isStateFarm) {
          const baseUrl = getOriginByCountry(policyCountry);
          return `${baseUrl}/Redirect/PolicyAction/${policyNumber}/viewdocs`;
        }

        return `${selfServiceOrigin}/documents/${policyNumber}`;
      },
      updatePolicy: (policyCountry) => {
        const baseUrl = getOriginByCountry(policyCountry);
        return `${baseUrl}/PolicyManagement/PolicySummary.aspx`;
      },
      getQuote: (siteCountry) => {
        const suffix =
          siteCountry === "USA" ? "quotemyclassic" : "quotemyclassic-ca";
        return `${hagertyOrigin}/apps/-/${suffix}`;
      },
      // Note: Per Praneet, always use .com for this. The .com URL shows a country selector when clicked.
      connectPolicy: (hasPolicy) =>
        `${authOrigin}/Connect/Customer?haspolicy=${hasPolicy}`,
      cancelPolicy: (policyCountry) => {
        const baseUrl = getOriginByCountry(policyCountry);
        return `${baseUrl}/PolicyManagement/CancelPolicy.aspx`;
      },
      billingStatements: (policyNumber) =>
        `${selfServiceOrigin}/documents/${policyNumber}/statements`,
      uploadDocuments: (policyCountry) => {
        const baseUrl = getOriginByCountry(policyCountry);
        return `${baseUrl}/Images2Upload/DocumentUpload.aspx`;
      },
      editCoverages: (policyCountry) => {
        const baseUrl = getOriginByCountry(policyCountry);
        return `${baseUrl}/PolicyManagement/ChangeCoverages.aspx`;
      },
      roadside:
        env === "prod"
          ? "https://hagerty.rsahelp.com/"
          : "https://hagerty.towupload.com/",
    },
    account: {
      changeAddress: `${hagertyOrigin}/PolicyManagement/ChangeAddress.aspx`,
      login: authOrigin,
      changeEmail: (country: Country) =>
        `${authOrigin}/ChangeEmail?ReturnCode=AccountProfile${
          country === "CANADA" ? "CA" : ""
        }`,
      changePassword: (country: Country) =>
        `${authOrigin}/Manage/ChangePassword?ReturnCode=AccountProfile${
          country === "CANADA" ? "CA" : ""
        }`,
      editPreferences: (contactId) =>
        `https://cloud.member.hagerty.com/editInfo?SubscriberKey=${contactId}`, // Yes, this is the same everywhere. Thus, this isn't even displayed in non-prod.
      marketplaceNotifications: `${usOrigin}/marketplace/my-garage/manage?notifications=true`,
      valuationNotificationPreferences: `${hagertyOrigin}/valuation-tools/notifications`,
      manageMfa: (country: Country) =>
        `${authOrigin}/identity/Manage/Mfa?ReturnCode=AccountProfile${
          country === "CANADA" ? "CA" : ""
        }`,
    },
    corporate: {
      accessibility: `${hagertyOrigin}/corporate/web-accessibility`,
      disclosures: `${hagertyOrigin}/disclosures`,
      privacyPolicy: `${hagertyOrigin}/corporate/privacy-policy`,
      termsOfUse: `${hagertyOrigin}/legal`,
      aboutUs: `${hagertyOrigin}/corporate`,
      marketplace: `${usOrigin}/marketplace`,
      marketplaceSell: `${usOrigin}/marketplace/sell`,
      agents:
        siteTld === "com"
          ? "https://www.hagertyagent.com"
          : "https://www.hagertybroker.ca",
      contactUs: `${hagertyOrigin}/corporate/contact-us`,
      faq: `${hagertyOrigin}/insurance/frequently-asked-questions`,
    },
    stateFarm: {
      claim: "https://reportloss.claims.statefarm.com",
    },
  };

  return urls;
}
